import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SBContext } from "../../component-controllers/SearchboxController.js";
import { useSearchboxStore } from "../../store/searchBoxStore.js";
import useValidation from "./useValidation.js";
export default function useSearchBox() {
    const oldContext = useContext(SBContext);
    const { actions, onTrackSBExperimentCustomGoal } = oldContext;
    const oldStore = useSearchboxStore();
    const { validate, focusFirstInvalidInput } = useValidation(!!oldContext.isEmptyDestinationExp);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const cabinClass = useMemo(() => oldContext.cabinClass, [oldContext.cabinClass]);
    const setCabinClass = useCallback((cabinClass) => {
        actions.setCabinClass(cabinClass);
    }, [actions]);
    const occupancy = useMemo(() => ({
        adults: oldContext.adults,
        children: oldContext.children
    }), [oldContext.adults, oldContext.children]);
    const setOccupancy = useCallback((value) => {
        onTrackSBExperimentCustomGoal({
            name: "flights_web_a11y_sb_v2_ddot",
            customGoal: 2
        });
        actions.setOccupancy(value);
    }, [actions, onTrackSBExperimentCustomGoal]);
    const segments = useMemo(() => oldContext.searchSegments, [oldContext.searchSegments]);
    const setSegmentTo = useCallback((locations, idx) => {
        actions.setTo(locations, idx);
    }, [actions]);
    const setSegmentFrom = useCallback((locations, idx) => {
        if (locations?.length > 1) {
            onTrackSBExperimentCustomGoal({
                name: "flights_web_a11y_sb_v2_ddot",
                customGoal: 1
            });
        }
        actions.setFrom(locations, idx);
    }, [actions, onTrackSBExperimentCustomGoal]);
    const removeSegment = useCallback((idx) => {
        actions.removeSegment(idx);
    }, [actions]);
    const swapSegment = useCallback((idx) => {
        actions.swapFromTo(idx);
        onTrackSBExperimentCustomGoal({
            name: "flights_web_a11y_sb_v2_ddot",
            customGoal: 4
        });
    }, [actions, onTrackSBExperimentCustomGoal]);
    const addSegment = useCallback(() => {
        actions.addSegment();
    }, [actions]);
    const clearSegments = useCallback(() => {
        actions.clearSegments();
    }, [actions]);
    const setIsDirect = useCallback((isDirect) => {
        actions.setIsDirect(isDirect);
    }, [actions]);
    const isDirect = useMemo(() => {
        return oldStore.searchbox.isDirect;
    }, [oldStore.searchbox.isDirect]);
    const setDate = useCallback((range, segmentIndex, fareRange) => {
        actions.setDateRange(range, segmentIndex, fareRange);
    }, [actions]);
    const searchType = useMemo(() => oldContext.type, [oldContext.type]);
    const cleanupBasedOnSearchType = useCallback((searchType) => {
        if (searchType === "MULTISTOP") {
            const segment0 = segments?.[0];
            if (segment0?.from?.length > 1 || segment0?.to?.length > 1) {
                const from = segment0?.from?.[0];
                const to = segment0?.to?.[0];
                setSegmentFrom(from ? [from] : [], 0);
                setSegmentTo(to ? [to] : [], 0);
            }
            if (segments.length === 1 && !oldContext?.config?.hideMultiCityEmptyInputs) {
                addSegment();
            }
        }
        else if (!oldStore.searchbox.initial) {
            clearSegments();
        }
    }, [
        addSegment,
        clearSegments,
        oldContext?.config?.hideMultiCityEmptyInputs,
        oldStore.searchbox.initial,
        segments,
        setSegmentFrom,
        setSegmentTo
    ]);
    const setSearchType = useCallback((searchType) => {
        setIsSubmitted(false);
        actions.setType(searchType);
        if (window.location.pathname.startsWith("/fly-anywhere")) {
            if (searchType === "MULTISTOP") {
                actions.setTo([], 0);
            }
        }
        cleanupBasedOnSearchType(searchType);
    }, [actions, cleanupBasedOnSearchType]);
    const doSearch = useCallback(() => {
        setIsSubmitted(true);
        if (searchType !== "MULTISTOP" && segments?.[0]?.to?.length === 0) {
            oldContext.onTrackSBExperimentCustomGoal({
                name: oldContext.isMobile ? "flights_web_cat_empty_destination_mdot" : "flights_web_cat_empty_destination_www",
                customGoal: 2
            });
        }
        if (validate(segments, occupancy, searchType)) {
            setIsValid(true);
            const etName = "flights_web_fare_calendar_v3";
            const type = searchType === "ONEWAY" ? "oneWay" : searchType === "ROUNDTRIP" ? "roundTrip" : undefined;
            const fareRange = type && oldContext.searchSegments[0].fareRange?.[type];
            fareRange && oldContext.onTrackSBExperimentCustomGoal({ name: etName, customGoal: fareRange == "LOW" ? 2 : 1 });
            oldContext.doSearch();
        }
        else {
            setIsValid(false);
            oldContext.onTrackSBExperimentCustomGoal({
                name: "flights_web_a11y_sb_v2_ddot",
                customGoal: 3
            });
            focusFirstInvalidInput();
        }
    }, [oldContext, validate, focusFirstInvalidInput, segments, occupancy, searchType]);
    const onChange = useCallback(() => {
        oldContext.onChange?.(oldContext.searchboxState);
    }, [oldContext]);
    useEffect(() => {
        setIsValid(validate(segments, occupancy, searchType));
    }, [searchType, segments, occupancy]);
    return {
        i18n: oldContext.i18n,
        autoCompleteBaseURL: oldContext.searchAutoCompleteBaseURL,
        lang: oldContext.lang,
        trackV2: oldContext.trackV2,
        dateFNSLocale: oldContext.dateFNSLocale,
        doSearch,
        onChange,
        isSubmitted,
        ipCountry: oldContext.ipCountry,
        searchType,
        setSearchType,
        cabinClass,
        setCabinClass,
        occupancy,
        setOccupancy,
        segments,
        setSegmentTo,
        setSegmentFrom,
        swapSegment,
        removeSegment,
        addSegment,
        setIsDirect,
        isDirect,
        setDate,
        isValid,
        trackLandingGoal: oldContext.trackLandingGoal,
        onTrackSBExperimentCustomGoal: oldContext.onTrackSBExperimentCustomGoal,
        onTrackSBExperimentStage: oldContext.onTrackSBExperimentStage,
        isHeaderFramed: !!oldContext.isHeaderFramed,
        hideShadow: !!oldContext.hideShadow,
        isMobile: !!oldContext.isMobile,
        isMeta: !!oldContext.isMeta,
        onGetSBExperimentVariant: oldContext.onGetSBExperimentVariant,
        onTrackSBExperiment: oldContext.onTrackSBExperiment,
        onTrackSBMetaExperimentStage: oldContext.onTrackSBMetaExperimentStage,
        disableAutocompleteMultiSelect: !!oldContext.hideLocationMultiSelect,
        isHeaderUpdateExp: !!oldContext.isHeaderUpdateExp,
        isHeaderOnDarkBg: !!oldContext.isHeaderOnDarkBg,
        normalizeHeaderPadding: !!oldContext.normalizeHeaderPadding,
        reorderControls: !!oldContext.reorderControls,
        isResponsiveFixes: !!oldContext.isResponsiveFixes,
        showNonStopExp: !!oldContext.showNonStopExp,
        fareCalendarURL: oldContext.fareCalendarURL,
        isFareCalendarEnabled: oldContext.isFareCalendarEnabled,
        isFareCalendarUIEnabled: oldContext.isFareCalendarUIEnabled,
        isSearchResults: oldContext.isSearchResults,
        formatPrice: oldContext.priceFormatter,
        routeName: oldContext.routeName,
        multiSelectVariant: oldContext.multiSelectVariant,
        multiSelectName: oldContext.multiSelectName,
        infantMaxAge: oldContext.infantMaxAge,
        fixInputSpacing: oldContext.fixInputSpacing,
        isDateDropdownOpen: oldContext.isDateDropdownOpen,
        enabledCountriesAndAnywhere: !!oldContext.enabledCountriesAndAnywhere,
        isEmptyDestinationExp: !!oldContext.isEmptyDestinationExp
    };
}
